import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * The motto that follows the brand name: تصحيح التلاوة.
 *
 * Always the exact brand lime, #C6C632. This is a DELIBERATE, owner-approved
 * exception to the contrast rule — on white it measures 1.82:1 — and a
 * darkened same-hue shade was built, shipped and rejected first because it
 * read as a different green from the lime in the brand's own artwork. The
 * motto is decorative: a reader who cannot make it out loses nothing. Do not
 * reuse the accent for text that carries meaning.
 */
export function BrandMotto({ className = '' }: { className?: string }) {
  const { t } = useLanguage();
  return (
    <span data-testid="brand-motto" className={`whitespace-nowrap text-accent ${className}`}>
      {t('brand.motto')}
    </span>
  );
}

/**
 * The mark and its motto on one row.
 *
 * assets/mark.png — the فصّلت mark itself, the same image the phone uses in
 * both its header and its sign-in lockup. NOT the association's wordmark,
 * which was deliberately taken off the sign-in screen.
 *
 * The mark, not the name set in type: the mark IS the brand's own lettering,
 * and setting 'فصّلت' in the UI font beside it was a second, plainer version
 * of the same word. The name survives as the image's alt text, so a screen
 * reader still announces it.
 */
export function Brand({ size = 'sm', stacked = false, mottoClassName = '' }: {
  size?: 'sm' | 'lg'; stacked?: boolean;
  /** Extra classes for the row motto, e.g. to drop it on a narrow bar that also holds the role switch. */
  mottoClassName?: string;
}) {
  const { t } = useLanguage();
  // Teal calligraphy on paper, ivory on night (redesign 2026-10).
  const mark = useTheme().scheme === 'dark' ? '/brand/mark-ivory.png' : '/brand/mark-teal.png';
  const height = size === 'lg' ? 'h-16' : 'h-8';
  const motto = size === 'lg' ? 'text-lg font-bold' : 'text-xl font-bold';
  if (stacked) {
    /*
     * The sign-in lockup, matching the phone's: the mark ABOVE its motto,
     * both centred, with the motto set large — it is the largest statement on
     * the page there, not a label riding alongside the mark. The in-app
     * header keeps the row, where the motto must not compete with anything.
     *
     * The gap is tighter than the surrounding stack's on purpose: the mark
     * and its motto are ONE lockup, and spacing them like two siblings pulls
     * them apart into two separate things.
     */
    return (
      <div className="flex flex-col items-center gap-1" data-testid="brand">
        <img src={mark} alt={t('brand.name')} className="h-20 w-auto" />
        <BrandMotto className="text-3xl font-bold leading-none" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3" data-testid="brand">
      <img src={mark} alt={t('brand.name')} className={`${height} w-auto`} />
      <BrandMotto className={`${motto} ${mottoClassName}`} />
    </div>
  );
}
